import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {useLocation, useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {
  detailsNavigationIdsAndValues,
  // detailsNavigationIds,
  onLoadScrollToElementTimeout,
  scrollToEl,
  navigationElementId,
} from '../../details.config';

import {
  hotelsAmenitiesSelector,
  roomDataHermesSelector,
  similarHotelsSelector,
  hotelPolicyDataSelector,
  // aboutPropertySelectorFn,
} from '../../selectors';

import getStickyOffsetY from '../../utils/get-sticky-offset-y';

import {snapData as snapDataSelectorFn} from '../../../UGC/redux/selectors';

import debounce from '../../../../../shared/utils/debounce';

const ItemLinkTo = styled.a(
  props => `
  text-decoration: none;
  color: ${props.active ? props.theme.colors.blue.base : props.theme.colors.grey.base};
  &:visited{
    color: ${props.active ? props.theme.colors.blue.base : props.theme.colors.grey.base};
  }
`,
);

const Navigation = () => {
  const [activeMenuItemId, setActiveMenuItemId] = useState('');
  const location = useLocation();
  const {hash, pathname, search} = location;
  const history = useHistory();
  const {all: amenitiesList} = useSelector(hotelsAmenitiesSelector);
  const roomsDataFromHermes = useSelector(roomDataHermesSelector);
  const snapData = useSelector(snapDataSelectorFn);
  const similarHotelsData = useSelector(similarHotelsSelector);

  const {hotelPolicyList = []} = useSelector(hotelPolicyDataSelector);

  // const thereAreSimilarHotels = !!similarHotelsData?.list.slice(1).length;

  const thereAreRooms = !!roomsDataFromHermes.length;

  const navigationRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // set active menu item from url hash, which helps with styles
    if (!hash) return;

    const allDataForPagePresent = !!amenitiesList.length && !!roomsDataFromHermes.length;

    /**
     * If there's a hash on details page, that means
     * we intend to be scrolled down to a particular element.
     * In order to make the user see that they are being scrolled down
     * we wait for 'onLoadScrollToElementTimeout' amount of timeout before scrolling.
     * If we do not do this, then the scroll starts in a flash (without the user being
     * able to see where the scrolling started from).
     *
     * TL;DR
     *
     * This time out is for user interaction and perceivability enhancement
     */
    let timerId = setTimeout(() => {
      // scroll to the element of which id is equal to hash in location url

      if (
        !document.querySelector(location.hash) ||
        /**ideally this should only check for data related to the container corresponding
         * to the hash as its id. But this should be fine as well
         */
        !allDataForPagePresent
      ) {
        return;
      }

      setActiveMenuItemId(hash);
      scrollToEl(location.hash);
      return () => clearTimeout(timerId);
    }, onLoadScrollToElementTimeout);

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [amenitiesList, roomsDataFromHermes, similarHotelsData, hotelPolicyList]);

  // https://css-tricks.com/sticky-smooth-active-nav/
  useEffect(() => {
    const allNavlinks = Array.from<HTMLAnchorElement>(
      document.querySelectorAll('#details-navigation a'),
    );

    // being super safe
    if (!allNavlinks.length) {
      return;
    }

    function updateActiveNavLink() {
      let fromTop = window.scrollY + getStickyOffsetY();

      allNavlinks.forEach(link => {
        let section: HTMLElement = document.querySelector(link.hash);

        if (!section) {
          return;
        }

        if (section.offsetTop <= fromTop && section.offsetTop + section.offsetHeight > fromTop) {
          setActiveMenuItemId(link.hash);
        }
      });
    }

    const debouncedFn = debounce(updateActiveNavLink, 150);

    window.addEventListener('scroll', updateActiveNavLink);

    return () => {
      window.addEventListener('remove', updateActiveNavLink);
      debouncedFn.cancel();
    };
  }, [amenitiesList, roomsDataFromHermes, similarHotelsData, hotelPolicyList]);

  return (
    <DetailsNavWrapper ref={navigationRef} id={navigationElementId}>
      <DetailNavInnerWrap>
        <NavList>
          {/* sync this up with current active and scroll with current same page link urls */}

          {Object.keys(detailsNavigationIdsAndValues).map((elementName, i) => {
            const id = detailsNavigationIdsAndValues[elementName].id;

            /**
             * TO PALAK/ANYONE:
             *
             * please also add handlings for guest reviews, RnR and About property
             * or any other data which is shown in navigation. Also please also add
             * these dependencies in the scroll handling useEffect above to update
             * the active link of the navigation menu, and also the URL scroll useEffect handling above it
             */

            // check if there's data to show for the navigation
            if (id === detailsNavigationIdsAndValues.amenities.id && !amenitiesList.length) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.rooms.id && !thereAreRooms) {
              return null;
            }

            // if (
            //   id === detailsNavigationIdsAndValues.guestReviews.id &&
            //   !(giDataExists || taDataExists)
            // ) {
            //   return null;
            // }

            // if (id === detailsNavigationIdsAndValues.QnA.id && !hasQnaData) {
            //   return null;
            // }

            // if (
            //   id === detailsNavigationIdsAndValues?.aboutProperty?.id &&
            //   !aboutPropertyData.length
            // ) {
            //   return null;
            // }

            // if (id === detailsNavigationIdsAndValues.similarHotels.id && !thereAreSimilarHotels) {
            //   return null;
            // }

            // if (id === detailsNavigationIdsAndValues.hotelPolicies.id && !hotelPolicyList.length) {
            //   return null;
            // }

            return (
              <NavListItem key={`item${i}`}>
                <ItemLinkTo
                  href={id}
                  active={!!(activeMenuItemId && activeMenuItemId === id)}
                  onClick={(e: Event) => {
                    e.preventDefault();
                    history.push(pathname + search + id);
                    scrollToEl(id);
                  }}
                >
                  {detailsNavigationIdsAndValues[elementName].value}
                </ItemLinkTo>
              </NavListItem>
              // <Item key={elementName} active={!!(activeMenuItemId && activeMenuItemId === id)}>
              //   <ItemLinkTo
              //     href={id}
              //     active={!!(activeMenuItemId && activeMenuItemId === id)}
              //     /**
              //      * NOTE: not extracting this function in a separate function
              //      * because it's a small function definition and is beneficial
              //      * to understand what is happening here itself. And also because
              //      * https://cdb.reacttraining.com/react-inline-functions-and-performance-bdff784f5578?gi=970e2972fe2e
              //      */
              //     onClick={(e: Event) => {
              //       /**
              //        * because we want to control scroll behavior
              //        * we prevent a tag to 'snapping' to the desired element
              //        * and instead manually push the history to (location + hash)
              //        */
              //       e.preventDefault();

              //       detailsPageClickGaPush({
              //         eventAction: DetailsGAClickEventActions.nav[id],
              //       });

              //       // current location + hash
              //       history.push(pathname + search + id);

              //       // set active menu item which helps with styles
              //       // setActiveMenuItemId(id);

              //       // scroll to desired element if it's there
              //       scrollToEl(id);
              //     }}
              //   >
              //     {detailsNavigationIdsAndValues[elementName].value}
              //   </ItemLinkTo>
              // </Item>
            );
          })}
        </NavList>
      </DetailNavInnerWrap>
    </DetailsNavWrapper>
  );
};

export default Navigation;

export const DetailsNavWrapper = styled.div(
  props => `
    position: sticky;
    top: 6.8rem;
    z-index: ${props.theme.misc.zIndex.large};
    background-color: ${props.theme.colors.white.bgWhite};
    width: 100%;
    display: flex;
    border-bottom: 0.7px solid ${props.theme.colors.grey.lightba};
    margin-top: ${props.theme.spacing.large.l30};
  `
);

export const DetailNavInnerWrap = styled.div(
  props => `
      max-width: 120rem;
      width: 100%;
      margin:0 auto;
      display: flex;
      padding: ${props.theme.spacing.large.l20} ${props.theme.spacing.nospacing};
  `
);

export const NavList = styled.ul(
  props => `
    width: 100%;
    display: flex;
    align-items: center;
    list-style-type: none;
  `
);

const NavListItem = styled.li(
  props => `
    width:20%;
    display: flex;
    text-transform: ${props.theme.font.textTransform.upper};
    color: ${props.theme.colors.grey.base};
    font-size: ${props.theme.font.large.font14};
    cursor: pointer;
    font-weight: ${props.theme.font.fontWeight.black};

    &:hover{
      color: ${props.theme.colors.blue.base};
    }

    &.active{
      color: ${props.theme.colors.blue.base};
    }
  `
)